import React from 'react';
import {Link} from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SafeIcon from '../common/SafeIcon';
import SEOHead from '../components/SEOHead';
import * as FiIcons from 'react-icons/fi';

const {FiFileText,FiMail,FiPhone,FiInfo,FiAlertTriangle}=FiIcons;

const TermsOfServicePage=()=> {
  return (
    <div className="min-h-screen bg-white">
      <SEOHead 
        title="Terms of Service - Diamond Cleaning Aylesbury"
        description="Diamond Cleaning Aylesbury terms of service and conditions. Service agreements, cancellation policy, and customer responsibilities."
        image="https://diamondcleaning-aylesbury.com/img/domestic-homepage.webp"
      />
      <Header currentArea="Aylesbury" />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-600 to-blue-800 pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <SafeIcon icon={FiFileText} className="w-16 h-16 text-white mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Terms of Service
            </h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto mb-8">
              These terms govern the use of our cleaning services and website. Please read them carefully before booking our services.
            </p>
            <div className="text-blue-100">
              <p>Last updated: January 2024</p>
            </div>
          </div>
        </div>
      </section>

      {/* Terms Content */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Introduction */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">1. Introduction and Acceptance</h2>
            <div className="prose prose-lg text-gray-700 space-y-4">
              <p>
                Welcome to Diamond Cleaning Aylesbury. These Terms of Service ("Terms") constitute a legally binding agreement between you ("Customer", "you", "your") and Diamond Cleaning Aylesbury ("Company", "we", "us", "our") regarding your use of our cleaning services and website.
              </p>
              <p>
                By booking our services, using our website, or engaging with us in any capacity, you acknowledge that you have read, understood, and agree to be bound by these Terms.
              </p>
              <div className="bg-blue-50 border-l-4 border-blue-600 p-4 rounded-r-lg">
                <div className="flex items-start space-x-3">
                  <SafeIcon icon={FiInfo} className="w-5 h-5 text-blue-600 mt-1" />
                  <div>
                    <p className="text-blue-800 font-medium">Important Notice</p>
                    <p className="text-blue-700 text-sm">
                      If you do not agree with these Terms, please do not use our services or website.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Services Description */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">2. Services Description</h2>
            <div className="prose prose-lg text-gray-700 space-y-4">
              <p>Diamond Cleaning Aylesbury provides professional cleaning services including:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Regular domestic cleaning (weekly, fortnightly, monthly)</li>
                <li>One-off domestic cleaning services</li>
                <li>Deep cleaning services</li>
                <li>End of tenancy cleaning</li>
                <li>Commercial cleaning (offices, retail, communal areas)</li>
                <li>Specialized cleaning services as agreed</li>
              </ul>
              <p>
                All services are provided by trained, insured, and vetted cleaning professionals in accordance with industry standards.
              </p>
            </div>
          </div>

          {/* Booking and Scheduling */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">3. Booking and Scheduling</h2>
            <div className="prose prose-lg text-gray-700 space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">3.1 Service Requests</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Services can be booked online, by phone, or email</li>
                  <li>All bookings are subject to availability and confirmation</li>
                  <li>We reserve the right to decline service requests at our discretion</li>
                  <li>Accurate property and contact information must be provided</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">3.2 Scheduling</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Service times are estimated and may vary based on property condition</li>
                  <li>We will provide advance notice of any significant schedule changes</li>
                  <li>Access to the property must be provided at the scheduled time</li>
                  <li>Regular services maintain consistent scheduling where possible</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">3.3 Minimum Service Requirements</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Domestic cleaning: 2-hour minimum service</li>
                  <li>Commercial cleaning: 3-hour minimum service</li>
                  <li>Deep cleaning and end of tenancy: As quoted</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Pricing and Payment */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">4. Pricing and Payment</h2>
            <div className="prose prose-lg text-gray-700 space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">4.1 Service Rates</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Standard hourly rate: £20 per hour</li>
                  <li>Rates may vary for specialized services or difficult access properties</li>
                  <li>All prices include VAT where applicable</li>
                  <li>Quotes are valid for 30 days unless otherwise stated</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">4.2 Payment Terms</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Payment is due upon completion of service</li>
                  <li>Accepted payment methods: Cash, bank transfer, card payments</li>
                  <li>Regular customers may arrange alternative payment schedules</li>
                  <li>Late payment charges may apply after 30 days</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">4.3 Additional Charges</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Travel charges may apply for properties outside standard service area</li>
                  <li>Additional time required due to property condition may incur extra charges</li>
                  <li>Key replacement or lockout assistance fees</li>
                  <li>Cancellation fees as outlined in Section 5</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Cancellation and Rescheduling */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">5. Cancellation and Rescheduling Policy</h2>
            <div className="prose prose-lg text-gray-700 space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">5.1 Customer Cancellations</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>24+ hours notice:</strong> No cancellation fee</li>
                  <li><strong>Less than 24 hours:</strong> 50% of service cost</li>
                  <li><strong>Same day cancellation:</strong> Full service cost</li>
                  <li><strong>No-shows:</strong> Full service cost plus travel expenses</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">5.2 Company Cancellations</h3>
                <p>
                  We may cancel services due to extreme weather, staff illness, or other circumstances beyond our control. In such cases, we will provide as much notice as possible and reschedule at no additional cost.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">5.3 Rescheduling</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Services can be rescheduled with 24+ hours notice at no charge</li>
                  <li>Less than 24 hours notice may incur rescheduling fees</li>
                  <li>We will accommodate rescheduling requests where possible</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Customer Responsibilities */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">6. Customer Responsibilities</h2>
            <div className="prose prose-lg text-gray-700 space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">6.1 Property Access and Preparation</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Ensure safe and secure access to the property</li>
                  <li>Provide working keys or access codes</li>
                  <li>Clear personal items and valuables from cleaning areas</li>
                  <li>Ensure adequate lighting and water supply</li>
                  <li>Inform us of any pets, alarms, or special requirements</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">6.2 Health and Safety</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Disclose any health hazards (asbestos, mold, chemicals)</li>
                  <li>Ensure the property is safe for our staff to work in</li>
                  <li>Report any damages or issues immediately</li>
                  <li>Provide accurate emergency contact information</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">6.3 Communication</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Respond promptly to scheduling confirmations</li>
                  <li>Provide feedback on service quality</li>
                  <li>Report any concerns within 24 hours of service completion</li>
                  <li>Keep contact information up to date</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Service Standards and Guarantees */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">7. Service Standards and Guarantees</h2>
            <div className="prose prose-lg text-gray-700 space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">7.1 Quality Guarantee</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>We guarantee professional cleaning standards on all services</li>
                  <li>If unsatisfied, contact us within 24 hours for a re-clean</li>
                  <li>End of tenancy cleaning includes deposit-back guarantee</li>
                  <li>We will return to address any legitimate concerns at no extra cost</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">7.2 Equipment and Supplies</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>We provide all necessary cleaning equipment and supplies</li>
                  <li>Eco-friendly products available upon request</li>
                  <li>Customer-supplied products can be used if preferred</li>
                  <li>We are not responsible for damage caused by customer-supplied products</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Liability and Insurance */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">8. Liability and Insurance</h2>
            <div className="prose prose-lg text-gray-700 space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">8.1 Insurance Coverage</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>We maintain comprehensive public liability insurance</li>
                  <li>All staff are covered under our insurance policy</li>
                  <li>Insurance details available upon request</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">8.2 Liability Limitations</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Liability is limited to the cost of the service provided</li>
                  <li>We are not liable for pre-existing damage or wear</li>
                  <li>Damage must be reported within 24 hours of service</li>
                  <li>We are not responsible for items not secured by the customer</li>
                </ul>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-lg">
                <div className="flex items-start space-x-3">
                  <SafeIcon icon={FiAlertTriangle} className="w-5 h-5 text-yellow-600 mt-1" />
                  <div>
                    <p className="text-yellow-800 font-medium">Important</p>
                    <p className="text-yellow-700 text-sm">
                      We cannot be held responsible for damage to items that are inherently fragile, antique, or of exceptional value unless specifically agreed in writing.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Data Protection and Privacy */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">9. Data Protection and Privacy</h2>
            <div className="prose prose-lg text-gray-700">
              <p>
                Your privacy is important to us. We collect, use, and protect your personal information in accordance with our Privacy Policy and applicable data protection laws including GDPR.
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-4">
                <li>Personal information is used solely for service delivery and communication</li>
                <li>We do not share your information with third parties without consent</li>
                <li>You have rights regarding your personal data as outlined in our Privacy Policy</li>
                <li>Security measures are in place to protect your information</li>
              </ul>
              <p className="mt-4">
                For full details, please refer to our <Link to="/privacy" className="text-blue-600 hover:text-blue-700 underline">Privacy Policy</Link>.
              </p>
            </div>
          </div>

          {/* Termination */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">10. Service Termination</h2>
            <div className="prose prose-lg text-gray-700 space-y-4">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">10.1 Customer Termination</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Regular services can be cancelled with 7 days written notice</li>
                  <li>No cancellation fees for regular service termination with proper notice</li>
                  <li>Outstanding invoices must be settled upon termination</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">10.2 Company Termination</h3>
                <p>We may terminate services immediately if:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Payment terms are not met</li>
                  <li>Property conditions are unsafe for our staff</li>
                  <li>Customer behavior is inappropriate or threatening</li>
                  <li>Terms of service are violated</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Dispute Resolution */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">11. Dispute Resolution</h2>
            <div className="prose prose-lg text-gray-700">
              <p>
                We are committed to resolving any disputes fairly and promptly:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-4">
                <li>Contact us immediately with any concerns or complaints</li>
                <li>We will investigate and respond within 48 hours</li>
                <li>Disputes will be handled in accordance with UK law</li>
                <li>Alternative dispute resolution may be pursued if necessary</li>
              </ul>
            </div>
          </div>

          {/* General Provisions */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">12. General Provisions</h2>
            <div className="prose prose-lg text-gray-700 space-y-4">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">12.1 Governing Law</h3>
                <p>
                  These Terms are governed by the laws of England and Wales. Any disputes will be subject to the exclusive jurisdiction of the English courts.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">12.2 Modifications</h3>
                <p>
                  We reserve the right to modify these Terms at any time. Updated Terms will be posted on our website with the revision date. Continued use of our services constitutes acceptance of any changes.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">12.3 Severability</h3>
                <p>
                  If any provision of these Terms is found to be unenforceable, the remaining provisions will continue in full force and effect.
                </p>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">13. Contact Information</h2>
            <div className="bg-gray-50 rounded-2xl p-8">
              <p className="text-gray-700 mb-6">
                For questions about these Terms of Service or our cleaning services, please contact us:
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Business Contact</h3>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <SafeIcon icon={FiPhone} className="w-5 h-5 text-blue-600" />
                      <a href="tel:07878522378" className="text-blue-600 hover:text-blue-700">
                        07878 522378
                      </a>
                    </div>
                    <div className="flex items-center space-x-3">
                      <SafeIcon icon={FiMail} className="w-5 h-5 text-blue-600" />
                      <a href="mailto:diamonddomesticcleaning1@gmail.com" className="text-blue-600 hover:text-blue-700">
                        diamonddomesticcleaning1@gmail.com
                      </a>
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Business Address</h3>
                  <address className="text-gray-700 not-italic">
                    Diamond Cleaning Aylesbury<br />
                    5 Ossulbury Lane<br />
                    Aylesbury HP19 9DW<br />
                    Buckinghamshire, England
                  </address>
                </div>
              </div>
              <div className="mt-6 text-center">
                <p className="text-sm text-gray-600">
                  Business Hours: Monday - Sunday, 9:00 AM - 8:00 PM
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TermsOfServicePage;